/**
 * Product Variants Navigator
 * Loads variant data from variants.json and enables navigation between related products
 */

class ProductVariantsNavigator {
  constructor() {
    this.variants = null;
    this.currentProductId = null;
    this.loaded = false;
  }

  /**
   * Initialize and load variants data
   */
  async init(productId) {
    this.currentProductId = productId;
    await this.loadVariants();
    return this.getRelatedVariants();
  }

  /**
   * Load variants from JSON file
   */
  async loadVariants() {
    if (this.loaded) return this.variants;

    try {
      console.log('📥 Loading variants data...');
      
      // First, try to load from embedded script tag
      const embeddedData = document.getElementById('variants-data-embedded');
      if (embeddedData) {
        try {
          this.variants = JSON.parse(embeddedData.textContent);
          this.loaded = true;
          console.log('✅ Loaded', Object.keys(this.variants).length, 'variants from embedded data');
          return this.variants;
        } catch (e) {
          console.warn('Failed to parse embedded variants data:', e);
        }
      }
      
      // Fallback: try to fetch from URLs
      const possibleUrls = [
        '/assets/variants.json',
        window.Shopify?.routes?.root ? `${window.Shopify.routes.root}variants.json` : '/variants.json',
        `${window.location.origin}/variants.json`
      ];
      
      let response = null;
      let successUrl = null;
      
      for (const url of possibleUrls) {
        try {
          response = await fetch(url);
          if (response.ok) {
            successUrl = url;
            break;
          }
        } catch (e) {
          console.log('Failed to fetch from:', url);
        }
      }
      
      if (!response || !response.ok) {
        console.warn('⚠️ Could not load variants.json from any location. Tried:', possibleUrls);
        return null;
      }
      
      this.variants = await response.json();
      this.loaded = true;
      console.log('✅ Loaded', Object.keys(this.variants).length, 'variants from', successUrl);
      return this.variants;
    } catch (error) {
      console.warn('❌ Error loading variants.json:', error);
      return null;
    }
  }

  /**
   * Get all related variants for the current product
   * Groups variants by their base product (excluding capacity/size differences)
   */
  getRelatedVariants() {
    if (!this.variants || !this.currentProductId) return [];

    const allVariants = Object.values(this.variants);
    
    // Find current product - try both shopify_id and product_id
    const currentProduct = allVariants.find(v => 
      v.shopify_id === this.currentProductId || 
      v.shopify_id === String(this.currentProductId) ||
      v.product_id === this.currentProductId ||
      v.product_id === String(this.currentProductId)
    );
    
    console.log('Current product found:', currentProduct);
    
    if (!currentProduct) {
      console.warn('No matching product found for ID:', this.currentProductId);
      return [];
    }

    // Get base name (remove capacity/size info)
    const baseName = this.getBaseName(currentProduct.name);
    console.log('Base name:', baseName);

    // Find all variants with same base name
    const relatedVariants = allVariants.filter(variant => {
      return this.getBaseName(variant.name) === baseName;
    });

    // Sort by capacity value
    return relatedVariants.sort((a, b) => {
      const capacityA = this.extractCapacityValue(a.dimensions?.['0'] || '');
      const capacityB = this.extractCapacityValue(b.dimensions?.['0'] || '');
      return capacityA - capacityB;
    });
  }

  /**
   * Extract base product name (remove capacity/size variants)
   */
  getBaseName(name) {
    // Remove capacity patterns like "1 TB", "2 TB", "500 GB", etc.
    // Also remove product codes in parentheses like "(WDBU6Y0050BBK-WESN)"
    return name
      .replace(/\d+\s*(TB|GB|MB)\s*/gi, '')
      .replace(/\([^)]*\)/g, '') // Remove anything in parentheses
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Extract numeric value from capacity string for sorting
   */
  extractCapacityValue(capacity) {
    const match = capacity.match(/(\d+)\s*(TB|GB|MB)/i);
    if (!match) return 0;
    
    const value = parseInt(match[1]);
    const unit = match[2].toUpperCase();
    
    // Convert to GB for consistent comparison
    if (unit === 'TB') return value * 1024;
    if (unit === 'MB') return value / 1024;
    return value; // GB
  }

  /**
   * Get the option name (e.g., "Capacity", "Memory", etc.)
   */
  getOptionName() {
    return 'Geheugencapaciteit'; // Dutch for Memory Capacity
  }

  /**
   * Get URL for a variant
   */
  getVariantUrl(variant) {
    return `/products/${variant.handle}`;
  }

  /**
   * Check if variant is the current product
   */
  isCurrentVariant(variant) {
    return variant.shopify_id === this.currentProductId || 
           variant.shopify_id === String(this.currentProductId) ||
           variant.product_id === this.currentProductId ||
           variant.product_id === String(this.currentProductId);
  }
}

// Initialize custom element for variant navigation
if (!customElements.get('product-variants-navigator')) {
  customElements.define(
    'product-variants-navigator',
    class ProductVariantsNavigatorElement extends HTMLElement {
      constructor() {
        super();
        this.navigator = new ProductVariantsNavigator();
      }

      async connectedCallback() {
        const productId = this.dataset.productId;
        console.log('🔍 Product Variants Navigator - Initializing for product ID:', productId);
        
        if (!productId) {
          console.warn('⚠️ Product Variants Navigator: No product ID provided');
          return;
        }

        const relatedVariants = await this.navigator.init(productId);
        console.log('📦 Related variants found:', relatedVariants);
        
        // Only render if there are multiple variants
        if (relatedVariants && relatedVariants.length > 1) {
          console.log('✅ Rendering variant navigator with', relatedVariants.length, 'variants');
          this.render(relatedVariants);
        } else {
          console.log('ℹ️ Not rendering - only', relatedVariants?.length || 0, 'variant(s) found');
        }
      }

      render(variants) {
        const optionName = this.navigator.getOptionName();
        
        const html = `
          <fieldset class="product-form__input product-form__input--variant-navigator">
            <legend class="form__label">${optionName}</legend>
            <div class="variant-navigator__options">
              ${variants.map(variant => this.renderOption(variant)).join('')}
            </div>
          </fieldset>
        `;
        
        this.innerHTML = html;
      }

      renderOption(variant) {
        const isCurrentVariant = this.navigator.isCurrentVariant(variant);
        const url = this.navigator.getVariantUrl(variant);
        const label = variant.dimensions?.['0'] || variant.name;
        const activeClass = isCurrentVariant ? 'variant-navigator__option--active' : '';
        
        return `
          <a 
            href="${url}" 
            class="variant-navigator__option ${activeClass}"
            ${isCurrentVariant ? 'aria-current="page"' : ''}
          >
            ${label}
          </a>
        `;
      }
    }
  );
}
